import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import SlideStage from "../components/SlideStage";
import {
  Play, Pause, ChevronLeft, ChevronRight, X, Volume2, CheckCircle2, XCircle,
  Loader2, Plane,
} from "lucide-react";

/**
 * LessonPlayerInteractive — slide-as-background + avatar overlay + animated
 * quiz popups. Built for the "Medium Turns (Interactive)" lesson where each
 * narration block carries a `slide_image` URL.
 *
 * UX modes:
 *   - teach:      slide is full-width, avatar is small bottom-right
 *   - quiz_enter: slide animates DOWN to a corner thumbnail, avatar swells to
 *                 centre, popup appears with multiple choice answers
 *   - quiz_done:  feedback overlay (correct / wrong + explanation), then either
 *                 next-block continue OR allow retry
 */
export default function LessonPlayerInteractive() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [blockIdx, setBlockIdx] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [freeText, setFreeText] = useState("");
  const [freeFeedback, setFreeFeedback] = useState(null); // {score, correct, feedback, model_answer}
  const [submitting, setSubmitting] = useState(false);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [progressId, setProgressId] = useState(null);
  // Set of block indices the user has answered correctly this session.  Used
  // by custom animated slides to remember which questions on the current
  // slide have already been resolved (so revealed answers stay visible when
  // the lesson advances to the next narration / quiz on the same slide).
  const [correctBlocks, setCorrectBlocks] = useState(() => new Set());
  // Per-blockIdx permutation of option indices so the correct answer isn't
  // always at position A.  Built lazily the first time we render that block;
  // persists for the lifetime of this lesson session.
  const shuffleRef = useRef({});
  const audioRef = useRef(null);

  // ---- Auth gate ----
  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // ---- Load lesson ----
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/lessons/${lessonId}`);
        setLesson(data);
        const { data: prog } = await api.post(`/progress/${lessonId}/start`);
        if (prog?.progress_id) setProgressId(prog.progress_id);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, lessonId]);

  const block = lesson?.blocks?.[blockIdx];
  const isNarration = block?.type === "narration";
  const isQuiz = block?.type === "quiz" || block?.type === "quiz_free";
  const isFreeText = block?.type === "quiz_free";
  const totalBlocks = lesson?.blocks?.length || 0;
  const pct = totalBlocks ? ((blockIdx + 1) / totalBlocks) * 100 : 0;

  // True when the user has just answered the current quiz block correctly —
  // this drives "test then reveal" slide animations (e.g. Slide 4 reveals the
  // four force arrows only after the student has correctly recalled them).
  const quizCorrect = isQuiz && quizSubmitted &&
    (isFreeText ? !!freeFeedback?.correct : selectedOpt === block?.answer);

  // When the student answers correctly, remember it so the slide-level
  // reveal sticks when we advance to the next narration on the same slide.
  useEffect(() => {
    if (quizCorrect) {
      setCorrectBlocks((s) => {
        if (s.has(blockIdx)) return s;
        const n = new Set(s);
        n.add(blockIdx);
        return n;
      });
    }
  }, [quizCorrect, blockIdx]);

  // For the current slide_index, build a flag list — one boolean per quiz on
  // the slide, in slide order — telling the custom slide component which of
  // its quizzes have been correctly answered so far this session.
  const slideQuizFlags = (() => {
    if (!lesson?.blocks || block?.slide_index == null) return [];
    return lesson.blocks
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => b.slide_index === block.slide_index && b.type === "quiz")
      .map(({ i }) => correctBlocks.has(i));
  })();

  // Per-blockIdx permutation of option indices so the correct answer isn't
  // always at the same position.  Built lazily the first time we render the
  // block; persists for the lifetime of this lesson session.
  const optionOrder = (() => {
    if (!isQuiz || isFreeText || !Array.isArray(block?.options)) return null;
    if (!shuffleRef.current[blockIdx]) {
      const idxs = block.options.map((_, i) => i);
      for (let i = idxs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
      }
      shuffleRef.current[blockIdx] = idxs;
    }
    return shuffleRef.current[blockIdx];
  })();

  // The avatar image — for now use the school instructor / captain image
  // referenced by this block's character id
  const avatarImg =
    "/api/files/topgun-academy/characters/captain.jpg";

  // ---- Fetch narration media when the block changes ----
  // Prefer the pre-rendered avatar VIDEO (HeyGen) — when it exists we don't
  // need a separate TTS audio track, the video carries the voice already.
  // Fallback to TTS audio + static portrait image if no video has been
  // rendered for this block yet.
  useEffect(() => {
    if (!lesson || !isNarration) return;
    setAudioUrl(null);
    setVideoUrl(null);
    setAudioProgress(0);
    setPlaying(false);
    setAudioLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data: v } = await api.get(
          `/lessons/${lessonId}/blocks/${blockIdx}/video`
        );
        if (cancelled) return;
        if (v?.video_url) {
          setVideoUrl(v.video_url);
          setAudioLoading(false);
          return;
        }
        // No video — fall back to TTS audio
        const { data } = await api.post(
          `/lessons/${lessonId}/blocks/${blockIdx}/audio`
        );
        if (!cancelled) setAudioUrl(data.audio_url);
      } catch (e) {
        console.error("media load failed", e);
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockIdx, lesson?.id]);

  // ---- Auto-play when media is ready ----
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [audioUrl]);

  // ---- Reset quiz state when entering a quiz block ----
  useEffect(() => {
    if (isQuiz) {
      setSelectedOpt(null);
      setQuizSubmitted(false);
      setFreeText("");
      setFreeFeedback(null);
    }
  }, [blockIdx, isQuiz]);

  // ---- Navigation ----
  const goNext = useCallback(() => {
    if (blockIdx < totalBlocks - 1) {
      setBlockIdx((i) => i + 1);
    } else {
      finishLesson();
    }
  }, [blockIdx, totalBlocks]);

  const goPrev = useCallback(() => {
    if (blockIdx > 0) setBlockIdx((i) => i - 1);
  }, [blockIdx]);

  const onAudioEnded = () => {
    setPlaying(false);
    setAudioProgress(1);
  };
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  // ---- Quiz submit ----
  const submitQuiz = async () => {
    if (selectedOpt == null || !block) return;
    setQuizSubmitted(true);
    const correct = selectedOpt === block.answer;
    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    try {
      await api.post(`/progress/${lessonId}/answer`, {
        block_index: blockIdx,
        answer_index: selectedOpt,
      });
    } catch (e) { /* ignore */ }
  };

  // ---- Free-text submit (Claude graded) ----
  const submitFreeText = async () => {
    if (!freeText.trim() || !block || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/progress/${lessonId}/answer-text`, {
        block_index: blockIdx,
        answer_text: freeText.trim(),
      });
      setFreeFeedback(data);
      setQuizSubmitted(true);
      setScore((s) => ({
        correct: s.correct + (data.correct ? 1 : 0),
        total: s.total + 1,
      }));
    } catch (e) {
      setFreeFeedback({ correct: false, score: 0,
        feedback: "Could not grade the answer right now. Try again.", });
    } finally {
      setSubmitting(false);
    }
  };

  const finishLesson = async () => {
    try {
      await api.post(`/progress/${lessonId}/complete`);
    } catch (e) { /* ignore */ }
    navigate(`/report/${lessonId}`);
  };

  if (!user || !lesson) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-white relative overflow-hidden"
      data-testid="lesson-interactive-page"
    >
      {/* HEADER STRIP */}
      <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-slate-950/95 via-slate-950/70 to-transparent">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/70 hover:text-white flex items-center gap-2"
            data-testid="exit-brief-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Exit
          </button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/60">
              {lesson.module}
            </div>
            <div className="font-heading font-bold text-base tracking-tight text-white">
              {lesson.title}
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/70">
            Block {blockIdx + 1} / {totalBlocks}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3">
          <Progress
            value={pct}
            className="h-1 bg-white/10 [&>div]:bg-[var(--brand-accent)] rounded-none"
            data-testid="lesson-progress"
          />
        </div>
      </div>

      {/* MAIN STAGE */}
      <div className="relative h-screen flex items-center justify-center pt-20 pb-32">
        {/* SLIDE BACKGROUND — animated based on mode.  When the student
            answers a quiz correctly, the slide expands back to ~70% width
            (above the quiz popup) so they can see the celebratory reveal. */}
        <div
          data-testid="slide-stage"
          className={`absolute transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            quizCorrect
              ? "top-[12vh] right-[5vw] w-[42vw] h-[44vh] rounded-sm opacity-100 z-20 shadow-2xl"
              : isQuiz
                ? "bottom-6 left-6 w-[22vw] h-[12.5vw] rounded-sm opacity-90 z-10 shadow-2xl"
                : "inset-0 m-auto w-[min(96vw,1400px)] h-[min(80vh,800px)] opacity-100 z-0"
          }`}
        >
          <div className={`w-full h-full bg-white rounded-sm overflow-hidden border border-slate-200 shadow-2xl transition-all duration-700 ${
            isQuiz && !quizCorrect ? "blur-[2px] brightness-50" : ""
          }`}>
            {block?.slide_image ? (
              <SlideStage
                slideIndex={block.slide_index}
                progress={audioProgress}
                quizCorrect={quizCorrect}
                isQuizBlock={isQuiz}
                slideQuizFlags={slideQuizFlags}
                fallbackImage={fileUrl(block.slide_image)}
              />
            ) : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-500">
                No slide
              </div>
            )}
          </div>
        </div>

        {/* AVATAR LAYER — corner thumbnail in teach mode, centred & large in quiz mode */}
        <div
          data-testid="avatar-stage"
          className={`absolute transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] z-20 ${
            isQuiz
              ? "right-[5vw] top-1/2 -translate-y-1/2 w-[32vw] max-w-[460px] aspect-[3/4]"
              : "right-8 bottom-32 w-[180px] h-[240px]"
          }`}
        >
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--brand-primary)]/40 via-transparent to-transparent rounded-sm pointer-events-none z-10" />
            {videoUrl && isNarration ? (
              <video
                ref={audioRef}
                src={fileUrl(videoUrl)}
                playsInline
                autoPlay
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (v.duration) setAudioProgress(v.currentTime / v.duration);
                }}
                onEnded={onAudioEnded}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                className={`relative z-0 w-full h-full object-cover rounded-sm border-2 transition-all duration-700 ${
                  isQuiz
                    ? "border-[var(--brand-accent)] shadow-[0_0_60px_rgba(255,163,0,0.4)]"
                    : "border-white/30 shadow-xl"
                }`}
                data-testid="avatar-video"
              />
            ) : (
              <img
                src={fileUrl(avatarImg)}
                alt=""
                className={`relative z-0 w-full h-full object-cover rounded-sm border-2 transition-all duration-700 ${
                  isQuiz
                    ? "border-[var(--brand-accent)] shadow-[0_0_60px_rgba(255,163,0,0.4)]"
                    : "border-white/30 shadow-xl"
                }`}
              />
            )}
            {isNarration && playing && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-sm bg-[var(--brand-accent)] text-slate-900 text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 whitespace-nowrap z-20" data-testid="speaking-badge">
                <Volume2 className="w-3 h-3" /> Speaking
              </div>
            )}
            {isQuiz && (
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-sm bg-[var(--brand-accent)] text-slate-900 text-[11px] font-bold uppercase tracking-[0.14em] whitespace-nowrap z-20" data-testid="question-badge">
                Question
              </div>
            )}
          </div>
        </div>

        {/* QUIZ POPUP — beside the avatar, capped at 80vh + scrollable body + sticky footer */}
        {isQuiz && (
          <div
            className="absolute z-30 left-[6vw] top-[6vh] bottom-[6vh] w-[min(50vw,640px)] fade-up flex"
            data-testid="quiz-popup"
          >
            <div className="bg-white text-slate-900 rounded-sm border border-slate-200 shadow-2xl overflow-hidden flex flex-col flex-1">
              {/* Header */}
              <div className="bg-[var(--brand-primary)] text-white px-5 py-3 flex items-center gap-2 flex-shrink-0">
                <Plane className="w-4 h-4 text-[var(--brand-accent)]" />
                <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">
                  {isFreeText ? "Open question" : "Quiz"} · Block {blockIdx + 1}
                </span>
              </div>

              {/* Scrollable body */}
              <div className="px-6 py-5 overflow-y-auto flex-1">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">
                  Question
                </div>
                <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight leading-tight" data-testid="quiz-question">
                  {block.question}
                </h3>

                {/* ---------- MCQ branch ---------- */}
                {!isFreeText && (
                  <>
                    <div className="mt-5 space-y-2.5">
                      {(optionOrder || block.options.map((_, i) => i)).map((origIdx, displayIdx) => {
                        const opt = block.options[origIdx];
                        const isSelected = selectedOpt === origIdx;
                        const isCorrect = origIdx === block.answer;
                        const showCorrect = quizSubmitted && isCorrect;
                        const showWrong = quizSubmitted && isSelected && !isCorrect;
                        return (
                          <button
                            key={origIdx}
                            onClick={() => !quizSubmitted && setSelectedOpt(origIdx)}
                            disabled={quizSubmitted}
                            className={`w-full text-left px-4 py-3 rounded-sm border-2 transition-all flex items-center gap-3 ${
                              showCorrect
                                ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                                : showWrong
                                ? "border-red-500 bg-red-50 text-red-900"
                                : isSelected
                                ? "border-[var(--brand-primary)] bg-[var(--brand-light)]"
                                : "border-slate-200 hover:border-slate-400 bg-white"
                            }`}
                            data-testid={`quiz-option-${displayIdx}`}
                          >
                            <span className="w-7 h-7 rounded-sm flex items-center justify-center text-xs font-bold bg-slate-100">
                              {String.fromCharCode(65 + displayIdx)}
                            </span>
                            <span className="flex-1 text-sm leading-snug">{opt}</span>
                            {showCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                            {showWrong && <XCircle className="w-5 h-5 text-red-600" />}
                          </button>
                        );
                      })}
                    </div>

                    {quizSubmitted && block.explanation && (
                      <div className="mt-5 p-4 bg-slate-50 border-l-2 border-[var(--brand-primary)] rounded-sm" data-testid="quiz-explanation">
                        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--brand-primary)] mb-1">
                          Explanation
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{block.explanation}</p>
                      </div>
                    )}
                  </>
                )}

                {/* ---------- Free-text branch ---------- */}
                {isFreeText && (
                  <>
                    {!quizSubmitted ? (
                      <>
                        <p className="mt-3 text-sm text-slate-500 italic">
                          Type your answer in your own words — Captain Hubert will grade it.
                        </p>
                        <textarea
                          value={freeText}
                          onChange={(e) => setFreeText(e.target.value)}
                          rows={6}
                          placeholder="e.g. First I do a lookout left, centre and right…"
                          className="mt-3 w-full p-3 border-2 border-slate-200 rounded-sm focus:border-[var(--brand-primary)] focus:outline-none text-sm leading-relaxed resize-none font-body"
                          data-testid="quiz-free-text-input"
                        />
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                          {freeText.trim().length} characters
                        </div>
                      </>
                    ) : (
                      freeFeedback && (
                        <div className="mt-5 space-y-3" data-testid="quiz-free-feedback">
                          <div className={`p-4 rounded-sm border-2 ${
                            freeFeedback.correct
                              ? "border-emerald-300 bg-emerald-50"
                              : "border-amber-300 bg-amber-50"
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {freeFeedback.correct
                                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                  : <XCircle className="w-5 h-5 text-amber-600" />}
                                <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-700">
                                  {freeFeedback.correct ? "Good answer" : "Almost there"}
                                </span>
                              </div>
                              <div className="font-heading font-bold text-2xl tabular-nums text-slate-900" data-testid="free-score">
                                {freeFeedback.score}%
                              </div>
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed mt-3">
                              {freeFeedback.feedback}
                            </p>
                          </div>
                          {freeFeedback.model_answer && (
                            <details className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                              <summary className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--brand-primary)] cursor-pointer">
                                See model answer
                              </summary>
                              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                                {freeFeedback.model_answer}
                              </p>
                            </details>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>{/* /scrollable body */}

              {/* Sticky footer */}
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 flex-shrink-0">
                  {!quizSubmitted ? (
                    isFreeText ? (
                      <Button
                        onClick={submitFreeText}
                        disabled={!freeText.trim() || submitting}
                        className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-semibold disabled:opacity-50"
                        data-testid="quiz-free-submit-btn"
                      >
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Grading…</> : "Submit answer"}
                      </Button>
                    ) : (
                      <Button
                        onClick={submitQuiz}
                        disabled={selectedOpt == null}
                        className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-semibold disabled:opacity-50"
                        data-testid="quiz-submit-btn"
                      >
                        Submit answer
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={goNext}
                      className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-semibold"
                      data-testid="quiz-continue-btn"
                    >
                      Continue <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* NARRATION TRANSCRIPT + AUDIO CONTROL — bottom drawer */}
      {isNarration && (
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-slate-950 via-slate-950/95 to-slate-950/0">
          <div className="max-w-5xl mx-auto px-6 py-5">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-accent)] mb-2">
              Narration
            </div>
            <p className="text-sm sm:text-base text-white/90 leading-relaxed line-clamp-3" data-testid="narration-text">
              {block.text}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={togglePlay}
                disabled={audioLoading || (!audioUrl && !videoUrl)}
                size="sm"
                className="bg-[var(--brand-accent)] hover:opacity-90 text-slate-900 font-semibold rounded-sm h-9 px-4 disabled:opacity-50"
                data-testid="play-pause-btn"
              >
                {audioLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : playing ? (
                  <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</>
                ) : (
                  <><Play className="w-3.5 h-3.5 mr-1" /> Play</>
                )}
              </Button>
              <div className="flex-1 relative h-1 bg-white/15 rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[var(--brand-accent)] transition-[width]"
                  style={{ width: `${audioProgress * 100}%` }}
                />
              </div>
              <Button
                onClick={goPrev}
                disabled={blockIdx === 0}
                size="sm"
                variant="outline"
                className="border-white/20 hover:border-white/50 text-white bg-transparent hover:bg-white/5 rounded-sm h-9 disabled:opacity-30"
                data-testid="prev-block-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={goNext}
                size="sm"
                className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm h-9 font-semibold"
                data-testid="next-block-btn"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {audioUrl && !videoUrl && (
              <audio
                ref={audioRef}
                src={fileUrl(audioUrl)}
                onTimeUpdate={(e) => {
                  const a = e.currentTarget;
                  if (a.duration) setAudioProgress(a.currentTime / a.duration);
                }}
                onEnded={onAudioEnded}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                preload="auto"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
